import {
  memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import BuffyActor, { createActor } from './buffy/BuffyActor';
import { useAtlas } from './buffy/atlas';
import Scene from './hero/Scene';
import { applyAnimator } from './hero/animate';
import {
  buildTrack, clipOf, localAt, postersOf, segIndexAt, stepIndexAt, stepsOf, STEP_IDS,
} from './hero/buffyTrack';
import { frameAt } from './buffy/clips';
import { heroLayout } from './hero/layout';
import { buildScene } from './hero/sceneTimeline';
import { STEPS, STORY_LABEL } from './storyScript';
import './hero/scene.css';
import { TeacherMotionContext } from './sections/useReveal';

/* StoryStage: the hero's clock and the stage it drives.

   CLOCK: one requestAnimationFrame loop adds elapsed ms to an absolute clock T
   while the story plays. Loop time t = T mod LOOP. Buffy's track is the master
   timeline (hero/buffyTrack.js); the scene timeline hangs off his contacts
   (hero/sceneTimeline.js). Every frame the stage writes transform / opacity
   for the elements that changed, and BuffyActor seeks his sprite. Because all
   of it is a pure function of t, a pause, a seek, the scene lab and the
   reduced-motion posters show the exact same frame.

   AUTO-PAUSE: offscreen stage (IntersectionObserver), hidden tab, or the
   pause button. Resuming re-syncs Buffy's sprite to the clock.

   AUDIT URLS (the lead's screenshots):
     ?frame=tile             a wallpaper tile behind the cards (default: plain
                             white, the owner's call on 2026-09-29)
     ?t=<ms>                 seek the loop to that ms and PAUSE
     ?step=<id>&settled=1    that step's poster, paused (Play resumes)
     ?step=<id>              play from that step
   REDUCED MOTION: no auto-advance; each step shows its poster and the progress
   segments switch steps. */

const MAX_FRAME_MS = 250;
const MASK_MS = 150;

export const TRACKS = { first: buildTrack({ firstRun: true }), loop: buildTrack({ firstRun: false }) };
export const LOOP_MS = TRACKS.first.loopMs;
export const STEP_WINDOWS = stepsOf(TRACKS.first);
export const POSTERS = postersOf(TRACKS.first);

const indexOfStep = (id) => Math.max(0, STEP_IDS.indexOf(id));

const REDUCED_QUERY = '(prefers-reduced-motion: reduce)';
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(REDUCED_QUERY).matches);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(REDUCED_QUERY);
    const onChange = () => setReduced(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return reduced;
}

const viewport = () => ({
  w: typeof document !== 'undefined' ? document.documentElement.clientWidth : 1440,
  h: typeof window !== 'undefined' ? window.innerHeight : 900,
  dpr: (typeof window !== 'undefined' && window.devicePixelRatio) || 1,
});

/* The hero's size tier and stage geometry, kept in step with the window. */
export function useHeroLayout() {
  const [vp, setVp] = useState(viewport);
  useEffect(() => {
    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setVp(viewport()));
    };
    window.addEventListener('resize', onResize);
    // a devicePixelRatio change (zoom, another screen) re-picks Buffy's scale
    let mq = null;
    const watch = () => {
      if (!window.matchMedia) return;
      mq = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
      if (mq.addEventListener) mq.addEventListener('change', onDpr);
    };
    function onDpr() {
      if (mq && mq.removeEventListener) mq.removeEventListener('change', onDpr);
      onResize();
      watch();
    }
    watch();
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      if (mq && mq.removeEventListener) mq.removeEventListener('change', onDpr);
    };
  }, []);
  return useMemo(() => heroLayout(vp.w, vp.h, vp.dpr), [vp.w, vp.h, vp.dpr]);
}

/* The driver for one stage DOM: finds the animated nodes once, then renders
   any clock time (or a poster) into them. */
function createController(root, scene, geo) {
  const byId = new Map();
  root.querySelectorAll('[data-a]').forEach((el) => {
    const id = el.getAttribute('data-a');
    if (!byId.has(id)) byId.set(id, []);
    byId.get(id).push(el);
  });
  const items = scene.animators
    .map((a) => ({ a, els: a.ids.flatMap((id) => byId.get(id) || []) }))
    .filter((it) => it.els.length);
  const cache = new Map();
  const actor = createActor(root, geo, TRACKS, scene);
  return {
    render(T, poster) {
      if (poster) {
        items.forEach((it) => applyAnimator(it.a, it.els, poster.t, cache));
        actor.pose(poster.buffy);
        return;
      }
      const run = Math.floor(T / scene.loopMs);
      const t = T - run * scene.loopMs;
      items.forEach((it) => applyAnimator(it.a, it.els, t, cache));
      actor.update(T, t, run);
    },
    resync() {
      actor.resync();
    },
    dispose() {
      actor.dispose();
    },
  };
}

/* One stage: the scene tree, Buffy, and (optionally) the progress row as a
   child. `source` is either the live engine or a fixed { T } / { poster }. */
export const StageView = memo(function StageView({
  geo, frame, source, paused, masking = false, children, stageRef, label = STORY_LABEL,
}) {
  const rootRef = useRef(null);
  const scene = useMemo(() => buildScene(geo, TRACKS.first), [geo]);
  // Buffy's sheets are one pre-decoded atlas; a still re-renders once it is in
  const atlas = useAtlas();

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const ctl = createController(root, scene, geo);
    let off = null;
    if (source && source.subscribe) off = source.subscribe(ctl);
    else if (source) ctl.render(source.T || 0, source.poster || null);
    return () => {
      if (off) off();
      ctl.dispose();
    };
  }, [scene, geo, source, atlas]);

  useEffect(() => {
    const scn = rootRef.current && rootRef.current.querySelector('.hx-scene');
    if (scn) scn.setAttribute('inert', '');
  }, []);

  const setRef = useCallback((el) => {
    rootRef.current = el;
    if (stageRef) stageRef.current = el;
  }, [stageRef]);

  return (
    <div
      ref={setRef}
      className="hx-stage cx-root"
      role="region"
      aria-label={label}
      data-frame={frame}
      data-kind={geo.kind}
      data-paused={String(!!paused)}
      data-masking={String(masking)}
      style={{ width: geo.stageW, height: geo.stageH }}
    >
      <ol className="cs-sr-only">
        {STEPS.map((s) => (
          <li key={s.id}>
            {`${s.label}.`}
            {s.teacher ? ` You type: “${s.teacher}”` : ''}
            {` Buffy: ${s.buffy}`}
          </li>
        ))}
      </ol>
      <div className="hx-scene" aria-hidden="true">
        <Scene geo={geo} frame={frame} scene={scene} />
        <BuffyActor s={geo.s} dev={geo.dev} dpr={geo.dpr} />
      </div>
      {children}
    </div>
  );
});

/* The live engine for the hero. */
export function useHeroEngine() {
  const [params] = useSearchParams();
  const frame = params.get('frame') === 'tile' ? 'tile' : 'plain';
  const stepParam = params.get('step');
  const settledParam = params.get('settled') === '1';
  const tRaw = params.get('t');
  const tParam = tRaw !== null && Number.isFinite(Number(tRaw)) ? Math.max(0, Number(tRaw)) : null;
  const reduced = usePrefersReducedMotion();
  // the clock waits until every Buffy sheet is decoded (no blank frames)
  const atlas = useAtlas();
  const buffyReady = atlas !== null;

  const initialT = () => {
    if (tParam !== null) return tParam;
    if (stepParam) return STEP_WINDOWS[indexOfStep(stepParam)].start;
    return 0;
  };
  const Tref = useRef(initialT());
  const listeners = useRef(new Set());
  const stageRef = useRef(null);
  const fillRefs = useRef([]);

  // poster: a step's still (reduced motion or ?settled=1). frozen: ?t= seek.
  const [posterStep, setPosterStep] = useState(() => (settledParam && stepParam ? indexOfStep(stepParam) : null));
  const [frozen, setFrozen] = useState(tParam !== null);
  const sharedMotion = useContext(TeacherMotionContext);
  const [localPaused, setLocalPaused] = useState(false);
  const userPaused = sharedMotion ? sharedMotion.paused : localPaused;
  const setUserPaused = sharedMotion ? sharedMotion.setPaused : setLocalPaused;
  const [inView, setInView] = useState(true);
  const [tabVisible, setTabVisible] = useState(() => typeof document === 'undefined' || document.visibilityState !== 'hidden');
  const [masking, setMasking] = useState(false);
  const [index, setIndex] = useState(() => stepIndexAt(STEP_WINDOWS, Tref.current % LOOP_MS));
  const indexRef = useRef(index);

  const posterMode = reduced || posterStep !== null;
  const shownPoster = posterMode ? (posterStep ?? index) : null;
  const playing = buffyReady && !posterMode && !frozen && !userPaused && inView && tabVisible && !masking;

  const writeProgress = useCallback((t, i) => {
    STEP_WINDOWS.forEach((w, j) => {
      let p = 0;
      if (j < i) p = 1;
      else if (j === i) p = posterMode ? 1 : Math.min(1, Math.max(0, (t - w.start) / (w.end - w.start)));
      const v = p.toFixed(3);
      // cached on the element itself: the one-bar progress row mounts a
      // fresh fill for each step, and a fresh fill must always be written
      const el = fillRefs.current[j];
      if (!el || el.dataset.fill === v) return;
      el.dataset.fill = v;
      el.style.transform = `scaleX(${v})`;
    });
  }, [posterMode]);

  const renderAll = useCallback((T) => {
    const poster = shownPoster !== null ? POSTERS[STEP_IDS[shownPoster]] : null;
    listeners.current.forEach((ctl) => ctl.render(T, poster));
    const t = poster ? poster.t : T % LOOP_MS;
    const i = stepIndexAt(STEP_WINDOWS, t);
    if (i !== indexRef.current && !poster) {
      indexRef.current = i;
      setIndex(i);
    }
    writeProgress(t, poster ? shownPoster : i);
  }, [shownPoster, writeProgress]);

  const subscribe = useCallback((ctl) => {
    listeners.current.add(ctl);
    ctl.resync();
    renderAll(Tref.current);
    return () => listeners.current.delete(ctl);
  }, [renderAll]);

  // The clock.
  useEffect(() => {
    listeners.current.forEach((ctl) => ctl.resync());
    renderAll(Tref.current);
    if (!playing) return undefined;
    let raf = 0;
    let last = null;
    const tick = (now) => {
      const raw = last === null ? 0 : now - last;
      last = now;
      if (raw > MAX_FRAME_MS) listeners.current.forEach((ctl) => ctl.resync());
      Tref.current += Math.min(raw, MAX_FRAME_MS);
      renderAll(Tref.current);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, renderAll]);

  useEffect(() => {
    const onVisibility = () => setTabVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting && entry.intersectionRatio >= 0.2),
      { threshold: [0, 0.2, 0.5] },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Audit params changed after mount (the lead moving between screenshots).
  const paramKey = `${stepParam || ''}|${settledParam ? 1 : 0}|${tParam ?? ''}`;
  const paramKeyRef = useRef(paramKey);
  useEffect(() => {
    if (paramKeyRef.current === paramKey) return;
    paramKeyRef.current = paramKey;
    Tref.current = initialT();
    setPosterStep(settledParam && stepParam ? indexOfStep(stepParam) : null);
    setFrozen(tParam !== null);
    setUserPaused(false);
    listeners.current.forEach((ctl) => ctl.resync());
    renderAll(Tref.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramKey]);

  /* A step-label click is a user-made cut: mask it with a 150 ms fade out
     and back of the whole stage, Buffy included (motion spec 3.5). */
  const maskTimer = useRef(0);
  useEffect(() => () => clearTimeout(maskTimer.current), []);
  const jump = useCallback((i) => {
    if (posterMode) {
      // stills switch instantly: nothing moves, so there is no cut to mask
      setPosterStep(i);
      return;
    }
    setMasking(true);
    clearTimeout(maskTimer.current);
    maskTimer.current = setTimeout(() => {
      const run = Math.floor(Tref.current / LOOP_MS);
      Tref.current = run * LOOP_MS + STEP_WINDOWS[i].start;
      setFrozen(false);
      setUserPaused(false);
      listeners.current.forEach((ctl) => ctl.resync());
      renderAll(Tref.current);
      setMasking(false);
    }, MASK_MS);
  }, [posterMode, renderAll, setUserPaused]);

  const togglePause = useCallback(() => {
    if (reduced) return;
    if (posterStep !== null) {
      Tref.current = STEP_WINDOWS[posterStep].start;
      setPosterStep(null);
      setUserPaused(false);
      return;
    }
    if (frozen) {
      setFrozen(false);
      setUserPaused(false);
      return;
    }
    setUserPaused((p) => !p);
  }, [reduced, posterStep, frozen, setUserPaused]);

  const source = useMemo(() => ({ subscribe }), [subscribe]);
  const renderRef = useRef(renderAll);
  renderRef.current = renderAll;

  // Dev-only audit hook (the route itself is dev-only): the clock, for the
  // builder's frame-capture checks (scratch/teacher-landing/buffy-diag.mjs).
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') return undefined;
    window.__hxClock = () => ({ T: Tref.current, loop: LOOP_MS, marks: TRACKS.first.marks });
    // render any absolute ms synchronously (use with ?t= so the clock is frozen)
    window.__hxRender = (T) => { Tref.current = T; renderRef.current(T); };
    window.__hxExpect = (T) => {
      const run = Math.floor(T / LOOP_MS);
      const t = T - run * LOOP_MS;
      const track = run === 0 ? TRACKS.first : TRACKS.loop;
      const seg = track.segs[segIndexAt(track, t)];
      const clip = clipOf(track, seg);
      return { clip: clip.name, frame: frameAt(clip, localAt(track, seg, t, T), seg.pose) };
    };
    return () => { delete window.__hxClock; delete window.__hxExpect; delete window.__hxRender; };
  }, []);

  return {
    frame,
    source,
    index: shownPoster ?? index,
    playing,
    paused: !playing,
    showPlay: posterStep !== null || frozen || userPaused,
    reduced,
    masking,
    jump,
    togglePause,
    stageRef,
    fillRefs,
  };
}
